import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@components/ui/collapsible';
import { TextField } from '@components/textfield';
import { Select } from '@components/select';

type QRLevel = 'L' | 'M' | 'Q' | 'H';

export interface QRConfig {
    size: number;
    level: QRLevel;
    marginSize: number;
}

interface QrCodeConfigProps {
    config: QRConfig;
    onConfigChange: (config: QRConfig) => void;
}

export const QrCodeConfig: React.FC<QrCodeConfigProps> = ({
    config,
    onConfigChange
}) => {
    const [expanded, setExpanded] = React.useState(false);

    const handleChange = (field: keyof QRConfig, value: number | string) => {
        onConfigChange({
            ...config,
            [field]: field === 'level' ? value : Number(value)
        });
    };

    return (
        <Card>
            <Collapsible open={expanded} onOpenChange={setExpanded}>
                <CollapsibleTrigger className="flex w-full items-center justify-between px-6 py-4 text-left">
                    <span className="text-sm font-medium">
                        QR Code Configuration
                    </span>
                    {expanded ? (
                        <ChevronUp className="size-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="size-4 text-muted-foreground" />
                    )}
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="flex flex-col gap-4 pt-0">
                        <TextField
                            label="Size (px)"
                            type="number"
                            value={config.size}
                            onChange={(e) => handleChange('size', e || 500)}
                            size="small"
                        />
                        <Select
                            label="Error Correction Level"
                            value={config.level}
                            onChange={(e) =>
                                handleChange('level', (e || 'L') as string)
                            }
                            options={[
                                { value: 'L', label: 'Low (7%)' },
                                { value: 'M', label: 'Medium (15%)' },
                                { value: 'Q', label: 'Quartile (25%)' },
                                { value: 'H', label: 'High (30%)' }
                            ]}
                            required
                        />
                        <TextField
                            label="Margin Size (modules)"
                            type="number"
                            value={config.marginSize}
                            onChange={(e) => handleChange('marginSize', e || 4)}
                            size="small"
                        />
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
};
